import "./_style.less";
import Space from "antd/lib/space";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  VuiContentLoading,
  VuiHelmet,
  VuiPagination,
  VuiSecondaryButton,
  VuiSpin,
  VuiTitle,
} from "../../../@vendor/components";
import PencilIcon from "../../../components/icons/Pencil";
import CalendarFullIcon from "../../../components/icons/CalendarFull";
import { Link, useNavigate } from "react-router-dom";
import InsightCard from "../../../components/molecules/Insight/Card";
import {
  ConstantService,
  formatDate,
  handleErrorLoadDataResponse,
  UrlParamsType,
  useIsMounted,
} from "../../../@vendor/utils";
import { useTranslation } from "react-i18next";
import ResearchRepository from "../../../repositories/ResearchRepository";
import ArticleRepository from "../../../repositories/ArticleRepository";
import CategoryRepository from "../../../repositories/CategoryRepository";

const Insight: React.FC<any> = () => {
  let navigate = useNavigate();
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [isFetchingData, setIsFetchingData] = React.useState<boolean>(false);
  const [research, setResearch] = React.useState<any[]>([]);
  const [articles, setArticles] = React.useState<any[]>([]);
  const [featured, setFeatured] = React.useState<any[]>([]);
  const [sideFeatured, setSideFeatured] = React.useState<any[]>([]);

  const [categoryList, setCategories] = React.useState<any[]>([]);
  const [totalCategoryData, setTotalCategoryData] = React.useState<number>(0);
  const [isLoadingCategory, setIsLoadingCategory] =
    React.useState<boolean>(false);
  const [categoryQueryParams, setCategoryQueryParams] = useState<UrlParamsType>(
    {
      page: Number(ConstantService.getConstant("DEFAULT_PAGE")),
      per_page: 6,
    }
  );

  const loadData = useCallback(async () => {
    setIsFetchingData(true);
    // CategoryRepository.select().then((response: any) => {
    //   console.log(response)
    // })

    ResearchRepository.findAll({
      per_page: 3,
      with: ["photo"],
      sorted_by: "desc",
      order_by: "date",
    })
      .then((response: any) => {
        if (isMounted) {
          const { data: responseData } = response.data;
          setResearch(responseData);
          setIsFetchingData(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setIsFetchingData(false);
          handleErrorLoadDataResponse(t, t("common.text.insight"));
        }
      });

    ArticleRepository.findAll({
      per_page: 3,
      with: ["photo"],
      sorted_by: "desc",
      order_by: "date",
    })
      .then((response: any) => {
        if (isMounted) {
          const { data: responseData } = response.data;
          setArticles(responseData);
          setIsFetchingData(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setIsFetchingData(false);
          handleErrorLoadDataResponse(t, t("common.text.insight"));
        }
      });

    ArticleRepository.highlight({ per_page: 3, with: ["photo"] })
      .then((response: any) => {
        if (isMounted) {
          const { data: responseData } = response.data;
          setFeatured(responseData.splice(0, 1));
          setSideFeatured(responseData.splice(1, 3));
          setIsFetchingData(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setIsFetchingData(false);
          handleErrorLoadDataResponse(t, t("common.text.insight"));
        }
      });
  }, [isMounted]);

  const onCategoryPageChange = useCallback(
    async (page: number, pageSize: number) => {
      setCategoryQueryParams((prevState) => ({
        ...prevState,
        page: page,
        per_page: pageSize,
      }));
    },
    []
  );

  const loadCategory = useCallback(async () => {
    setIsLoadingCategory(true);
    CategoryRepository.findAll({ ...categoryQueryParams, with: ["icon"] })
      .then((response: any) => {
        if (isMounted) {
          const { data: responseData, meta } = response.data;
          setCategories(responseData);
          setTotalCategoryData(meta.total);
          setIsLoadingCategory(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setIsLoadingCategory(false);
          handleErrorLoadDataResponse(t, t("common.text.insight"));
        }
      });
  }, [categoryQueryParams]);

  useMemo(() => {
    (async () => {
      await loadData();
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await loadCategory();
    })();
  }, [categoryQueryParams]);

  if (isFetchingData) {
    return <VuiContentLoading loading={isFetchingData} />;
  }

  return (
    <>
      <VuiHelmet title={t("common.text.insight")} />
      <div id="insight-section" className="section">
        <div className="container">
          <Space direction="vertical" size={50}>
            <VuiTitle text={"Insight"} />

            <div>
              <div className="featured-section">
                {featured.map((item: any) => (
                  <Link
                    to={`/insight/${item.permalink || item.id}`}
                    className="big-card"
                  >
                    <div className="image-wrapper">
                      <img
                        className="image"
                        src={
                          item.photo?.url ||
                          "data:image/png;base64,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"
                        }
                        alt="insight"
                      />
                    </div>
                    <div className="info-wrapper">
                      <label className="category">{item.first_tag}</label>
                      <h3 className="title">{item.title}</h3>
                      <p className="summary">{item.summary}</p>
                      <ul className="icon-vertical-list">
                        <li className="item">
                          <CalendarFullIcon className="icon" />
                          <label className="text">
                            {formatDate(item.date)}
                          </label>
                        </li>
                        <li className="item">
                          <PencilIcon className="icon" />
                          <label className="text">{item.author_name}</label>
                        </li>
                      </ul>
                    </div>
                  </Link>
                ))}

                <div className="side-featured">
                  {sideFeatured.map((item: any) => (
                    <InsightCard
                      key={`featured-${item.id}`}
                      id={item.id}
                      link={`/insight/${item.permalink || item.id}`}
                      image={item.photo?.url}
                      label={item.first_tag}
                      name={item.title}
                      summary={item.summary}
                      date={formatDate(item.date)}
                      author={item.author_name}
                    />
                  ))}
                </div>
              </div>

              <div className="more-section">
                <div className="more-wrapper">
                  <div>
                    <h3 className="section-title">Articles</h3>

                    <div className="more-list">
                      {articles.map((item: any) => (
                        <InsightCard
                          key={`article-${item.id}`}
                          id={item.id}
                          link={`/insight/${item.permalink || item.id}`}
                          image={item.photo?.url}
                          label={item.first_tag}
                          name={item.title}
                          summary={item.summary}
                          date={formatDate(item.date)}
                          author={item.author_name}
                        />
                      ))}
                    </div>

                    <div className="more-btn-wrapper">
                      <VuiSecondaryButton
                        label={"See More"}
                        onClick={() => navigate("../articles")}
                      />
                    </div>
                  </div>
                  <div>
                    <h3 className="section-title">Research</h3>

                    <div className="more-list">
                      {research.map((item: any) => (
                        <InsightCard
                          key={`research-${item.id}`}
                          id={item.id}
                          link={item.issuu_link}
                          image={item.photo?.url}
                          label={item.first_tag}
                          name={item.title}
                          summary={item.summary}
                          date={formatDate(item.date)}
                          author={item.author_name}
                          isResearch={true}
                        />
                      ))}
                    </div>

                    <div className="more-btn-wrapper">
                      <VuiSecondaryButton
                        label={"See More"}
                        onClick={() => navigate("../research")}
                      />
                    </div>
                  </div>
                </div>
                <div className="category-wrapper">
                  <h3 className="section-title">Category</h3>

                  {isLoadingCategory ? (
                    <div className="spin-category-wrapper">
                      <VuiSpin size="large" spinning={isLoadingCategory} />
                    </div>
                  ) : (
                    <>
                      <ul className="category-list">
                        {categoryList.map((item, key) => {
                          return (
                            <li className="item" key={key}>
                              <Link
                                to={`/insight/articles?tags=${item.id}`}
                                className="card-category"
                              >
                                <div className="icon-wrapper">
                                  <img
                                    className="icon"
                                    src={item.icon?.url}
                                    alt="mental health icon"
                                  />
                                </div>
                                <label className="name">{item.name}</label>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="category-pagination-wrapper">
                        <VuiPagination
                          onChange={onCategoryPageChange}
                          total={totalCategoryData}
                          current={Number(categoryQueryParams.page)}
                          pageSize={Number(categoryQueryParams.per_page)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </Space>
        </div>
      </div>
    </>
  );
};

export default Insight;
